
import { useState, useEffect } from "react";
import { getAlertSettings, setAlertSettings } from "@/lib/storage/alertStorage";
import { AlertSettings } from "@/lib/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Bell, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { getCurrentUser } from "@/lib/supabase";
import NotificationToggle from "./notifications/NotificationToggle";
import ThresholdSetting from "./notifications/ThresholdSetting";
import NotificationMethods from "./notifications/NotificationMethods";

const NotificationSettings = () => {
  const [settings, setSettings] = useState<AlertSettings>(getAlertSettings());
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Load settings on component mount
    setSettings(getAlertSettings());
    
    // Load user email
    const loadUserEmail = async () => {
      const user = await getCurrentUser();
      setUserEmail(user?.email || null);
    };
    
    loadUserEmail();
  }, []);

  const handleToggleNotifications = (enabled: boolean) => {
    const updatedSettings = { ...settings, enabled };
    setSettings(updatedSettings);
    setAlertSettings(updatedSettings);
    
    toast({
      title: enabled ? "Notifications Enabled" : "Notifications Disabled",
      description: enabled 
        ? "You'll receive alerts when products are running low" 
        : "You won't receive low stock alerts",
      duration: 3000,
    });
  };

  const handleToggleEmailNotifications = (emailNotifications: boolean) => {
    const updatedSettings = { ...settings, emailNotifications };
    setSettings(updatedSettings);
    setAlertSettings(updatedSettings);
    
    if (emailNotifications) {
      toast({
        title: "Email Notifications Enabled",
        description: "You'll receive email alerts when products are running low",
        duration: 3000,
      });
    }
  };

  const handleToggleInAppNotifications = (phoneNotifications: boolean) => {
    const updatedSettings = { ...settings, phoneNotifications };
    setSettings(updatedSettings);
    setAlertSettings(updatedSettings);
    
    if (phoneNotifications) {
      toast({
        title: "In-app Notifications Enabled",
        description: "You'll receive in-app notifications when products are running low",
        duration: 3000,
      });
    }
  };

  const handleThresholdChange = (daysThreshold: number) => {
    const updatedSettings = { ...settings, daysThreshold };
    setSettings(updatedSettings);
    setAlertSettings(updatedSettings);
  };

  const handleSaveSettings = () => {
    setAlertSettings(settings);
    toast({
      title: "Settings Saved",
      description: "Your notification preferences have been updated",
      duration: 3000,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bell className="mr-2 h-5 w-5 text-primary" />
          Notification Settings
        </CardTitle>
        <CardDescription>
          Configure how and when you want to be notified about low stock
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <NotificationToggle 
          id="notifications-enabled"
          icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
          title="Enable Low Stock Alerts"
          checked={settings.enabled}
          onCheckedChange={handleToggleNotifications}
        />
        
        <ThresholdSetting
          value={settings.daysThreshold}
          onChange={handleThresholdChange}
          disabled={!settings.enabled}
        />
        
        <NotificationMethods
          settings={settings}
          onToggleEmailNotifications={handleToggleEmailNotifications}
          onToggleInAppNotifications={handleToggleInAppNotifications}
          userEmail={userEmail}
        />
      </CardContent>
      <CardFooter>
        <Button onClick={handleSaveSettings}>
          Save Settings
        </Button>
      </CardFooter>
    </Card>
  );
};

export default NotificationSettings;
